'use client'

import { useEffect, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ArrowLeftRight } from 'lucide-react'
import { DemoFrame } from './demoKit'
import { useOnScreen } from './demoHooks'

// API integrations: GoHighLevel in the middle of the tools a business already
// uses. Picking a tool (or letting it cycle) sends data both ways along that
// line, and the log underneath says what each sync actually did: the
// "bidirectional syncs" and "webhooks into workflows" the page describes.

const W = 520
const H = 250
const HUB = { x: W / 2, y: H / 2 }
const APPS = [
  { key: 'qb', name: 'QuickBooks', short: 'QB', color: '#2ca01c', x: 70, y: 52,
    inbound: 'Invoice paid in QuickBooks', inEffect: 'Deal moved to Won, contact tagged "paid"',
    outbound: 'New client won in GoHighLevel', outEffect: 'Customer created in QuickBooks' },
  { key: 'slack', name: 'Slack', short: 'SL', color: '#4a154b', x: 70, y: 198,
    inbound: 'Rep replies "on it" in Slack', inEffect: 'Task marked as in progress',
    outbound: 'Hot lead arrives in GoHighLevel', outEffect: '#sales channel alerted with the details' },
  { key: 'shop', name: 'Shopify', short: 'SH', color: '#5e8e3e', x: 260, y: 26,
    inbound: 'Order placed on Shopify', inEffect: 'Contact created + post-purchase workflow', outbound: 'Review request sent', outEffect: 'Order note added in Shopify' },
  { key: 'click', name: 'ClickUp', short: 'CU', color: '#7b68ee', x: 450, y: 52,
    inbound: 'Project task completed', inEffect: 'Client notified by text', outbound: 'Deal signed in GoHighLevel', outEffect: 'Onboarding project created' },
  { key: 'sheets', name: 'Google Sheets', short: 'GS', color: '#0f9d58', x: 450, y: 198,
    inbound: 'Row added by the sales team', inEffect: 'Contact upserted, no duplicates', outbound: 'Pipeline changes each night', outEffect: 'Reporting sheet refreshed' },
  { key: 'zap', name: 'Zapier / Make', short: 'ZP', color: '#ff4f00', x: 260, y: 224,
    inbound: 'Webhook from your booking tool', inEffect: 'Appointment workflow triggered', outbound: 'Form submitted in GoHighLevel', outEffect: 'Sent on to the tools you already run' },
]

export default function IntegrationHub() {
  const [ref, onScreen] = useOnScreen()
  const reduce = useReducedMotion()
  const [index, setIndex] = useState(0)
  const [auto, setAuto] = useState(true)
  const app = APPS[index]

  useEffect(() => {
    if (!auto || !onScreen) return undefined
    const timer = setTimeout(() => setIndex((i) => (i + 1) % APPS.length), 4200)
    return () => clearTimeout(timer)
  }, [auto, onScreen, index])

  const pick = (i) => { setAuto(false); setIndex(i) }

  return (
    <DemoFrame frameRef={ref} className="svd-hub" title="Your stack, kept in sync" label="Demo: GoHighLevel syncing data both ways with QuickBooks, Slack, Shopify, ClickUp, Google Sheets and Zapier">
      <div className="svd-hub-map">
        <svg viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
          {APPS.map((a, i) => (
            <line key={a.key} x1={HUB.x} y1={HUB.y} x2={a.x} y2={a.y} className={i === index ? 'is-on' : ''} />
          ))}
          {onScreen && !reduce ? (
            <g key={`${app.key}-packets`}>
              <motion.circle r="5" fill={app.color} initial={{ cx: app.x, cy: app.y, opacity: 0 }} animate={{ cx: [app.x, HUB.x], cy: [app.y, HUB.y], opacity: [0, 1, 1, 0] }} transition={{ duration: 1.1, repeat: Infinity, repeatDelay: 0.4 }} />
              <motion.circle r="5" fill="#1684ea" initial={{ cx: HUB.x, cy: HUB.y, opacity: 0 }} animate={{ cx: [HUB.x, app.x], cy: [HUB.y, app.y], opacity: [0, 1, 1, 0] }} transition={{ duration: 1.1, delay: 0.75, repeat: Infinity, repeatDelay: 0.4 }} />
            </g>
          ) : null}
        </svg>
        <span className="svd-hub-core" style={{ left: `${(HUB.x / W) * 100}%`, top: `${(HUB.y / H) * 100}%` }}>
          <b>GoHighLevel</b>
          <small>your CRM</small>
        </span>
        {APPS.map((a, i) => (
          <button
            key={a.key}
            type="button"
            className={`svd-hub-app${i === index ? ' is-on' : ''}`}
            style={{ left: `${(a.x / W) * 100}%`, top: `${(a.y / H) * 100}%`, '--app': a.color }}
            onClick={() => pick(i)}
            aria-pressed={i === index}
          >
            <span className="svd-hub-logo" aria-hidden="true">{a.short}</span>
            <span className="svd-hub-name">{a.name}</span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={app.key} className="svd-hub-log" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.25 }}>
          <div>
            <span className="svd-hub-dir" style={{ '--app': app.color }}>{app.name} → GoHighLevel</span>
            <p><b>{app.inbound}</b><span>{app.inEffect}</span></p>
          </div>
          <ArrowLeftRight size={16} className="svd-hub-swap" aria-hidden="true" />
          <div>
            <span className="svd-hub-dir is-ghl">GoHighLevel → {app.name}</span>
            <p><b>{app.outbound}</b><span>{app.outEffect}</span></p>
          </div>
        </motion.div>
      </AnimatePresence>
    </DemoFrame>
  )
}
